import styled from '@emotion/styled';
import { msg } from '@lingui/core/macro';
import { Trans, useLingui } from '@lingui/react/macro';
import type { FabricEditor } from 'modules/fabric-editor';
import type { LayoutBlockData, LayoutBlockFitMode, LayoutPreset } from 'modules/manager/domain/layout-blocks';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { editorAccent } from '../editor-accent';
import { LayoutBlocksModal } from '../layout-blocks-modal';
import CommitTextField from './inspectors/shared/commit-text-field';
import InspectorRow from './inspectors/shared/inspector-row';
import InspectorSection from './inspectors/shared/inspector-section';
import SelectField from './inspectors/shared/select-field';
import { theme } from 'styles/theme/theme';

interface LayoutBlocksPanelProps {
  editor: FabricEditor;
}

const FIT_MODE_OPTIONS = [
  { value: 'cover', label: msg`Заполнение (обрезка)` },
  { value: 'contain', label: msg`Вписать (без обрезки)` },
];

const LayoutBlocksPanel: React.FC<LayoutBlocksPanelProps> = ({ editor }) => {
  const { t } = useLingui();
  const facade = editor.layoutBlocks;
  const [data, setData] = useState<LayoutBlockData | undefined>(() => facade?.getData());
  const [showModal, setShowModal] = useState(false);

  const refresh = useCallback(() => {
    setData(facade?.getData());
  }, [facade]);

  useEffect(() => {
    if (!facade) return;
    refresh();
    const offAdded = editor.on('object:added', refresh);
    const offRemoved = editor.on('object:removed', refresh);
    const offModified = editor.on('object:modified', refresh);
    return () => {
      offAdded();
      offRemoved();
      offModified();
    };
  }, [editor, facade, refresh]);

  const presets: LayoutPreset[] = useMemo(() => facade?.listPresets() ?? [], [facade]);
  const currentPreset = useMemo(() => (data ? presets.find((p) => p.id === data.presetId) : undefined), [data, presets]);

  if (!facade) return null;

  const fitModeOptions = FIT_MODE_OPTIONS.map((option) => ({ value: option.value, label: t(option.label) }));

  if (!data) {
    return (
      <>
        <InspectorSection title={t`Блоки макета`}>
          <Hint>
            <Trans>Нет фонового макета. Выберите пресет, чтобы добавить.</Trans>
          </Hint>
          <PrimaryButton type="button" onClick={() => setShowModal(true)}>
            <Trans>Добавить макет…</Trans>
          </PrimaryButton>
        </InspectorSection>
        <LayoutBlocksModal show={showModal} onClose={() => setShowModal(false)} presets={presets} onPick={(id) => facade.apply(id)} />
      </>
    );
  }

  return (
    <>
      <InspectorSection title={t`Блоки макета`}>
        <InspectorRow label={t`Пресет`}>
          <Value>{currentPreset ? t(currentPreset.label) : data.presetId}</Value>
        </InspectorRow>
        <Buttons>
          <SecondaryButton type="button" onClick={() => setShowModal(true)}>
            <Trans>Заменить…</Trans>
          </SecondaryButton>
          <DangerButton type="button" onClick={() => facade.remove()}>
            <Trans>Удалить</Trans>
          </DangerButton>
        </Buttons>
        <InspectorRow label={t`Прозрачность`}>
          <OpacityControl>
            <input
              type="range"
              min={0}
              max={100}
              step={1}
              value={Math.round(data.opacity * 100)}
              onChange={(e) => facade.setOpacity(Number(e.target.value) / 100)}
            />
            <OpacityValue>{Math.round(data.opacity * 100)}%</OpacityValue>
          </OpacityControl>
        </InspectorRow>
      </InspectorSection>
      <InspectorSection title={t`Блоки`}>
        {data.blocks.map((block, i) => {
          const blockNumber = i + 1;
          return (
            <BlockRow key={block.placeholderKey}>
              <BlockHeader>
                <Trans>Блок {blockNumber}</Trans>
              </BlockHeader>
              <InspectorRow label={t`Подпись`}>
                <CommitTextField value={block.placeholderLabel} onCommit={(v) => facade.setBlockLabel(i, v)} />
              </InspectorRow>
              <InspectorRow label={t`Вписывание`}>
                <SelectField
                  value={block.fitMode}
                  onChange={(v) => facade.setBlockFitMode(i, v as LayoutBlockFitMode)}
                  options={fitModeOptions}
                />
              </InspectorRow>
            </BlockRow>
          );
        })}
      </InspectorSection>
      <LayoutBlocksModal
        show={showModal}
        onClose={() => setShowModal(false)}
        presets={presets}
        currentPresetId={data.presetId}
        onPick={(id) => facade.apply(id)}
      />
    </>
  );
};

export default React.memo(LayoutBlocksPanel);

const Hint = styled.p`
  font-size: 12px;
  color: ${theme.colors.Gray};
  margin: 0;
`;

const Value = styled.span`
  font-size: 13px;
  color: ${theme.colors.DarkBlue600};
  font-weight: 500;
`;

const Buttons = styled.div`
  display: flex;
  gap: 6px;
`;

const baseButton = `
  flex: 1 1 auto;
  height: 30px;
  min-height: 30px;
  max-height: 30px;
  padding: 0 10px;
  border-radius: 6px;
  font-size: 12px;
  cursor: pointer;
`;

const PrimaryButton = styled.button`
  ${baseButton}
  background: ${editorAccent.solid};
  color: ${editorAccent.onSolid};
  border: none;

  &:hover {
    background: ${editorAccent.solidHover};
  }
`;

const SecondaryButton = styled.button`
  ${baseButton}
  background: ${theme.colors.SurfaceCultured10};
  color: ${theme.colors.DarkBlue600};
  border: 1px solid ${theme.colors.DarkBlue100};

  &:hover {
    background: ${theme.colors.DarkBlue100};
  }
`;

// Повторяет вариант `danger` из ud-ui/components/icon-button: спокойная кнопка
// с красной подписью, заливка красным только на hover/active. Здесь это текстовая
// кнопка, а не иконочная, поэтому UDIconButton не переиспользуется напрямую —
// рядом стоит текстовый «Заменить…», и пара должна выглядеть однородно.
const DangerButton = styled.button`
  ${baseButton}
  background: ${theme.colors.SurfaceWhite};
  color: ${theme.colors.error};
  border: 1px solid ${theme.colors.WhiteGray50};
  transition: all 0.2s ease-in-out;

  &:hover {
    background: ${theme.colors.error};
    border-color: ${theme.colors.error};
    color: ${theme.colors.SurfaceWhite};
  }

  &:active {
    background: ${theme.colors.Red};
    border-color: ${theme.colors.Red};
    color: ${theme.colors.SurfaceWhite};
  }
`;

const OpacityControl = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;

  input[type='range'] {
    flex: 1;
    min-width: 0;
  }
`;

const OpacityValue = styled.span`
  font-size: 12px;
  color: ${theme.colors.Gray};
  min-width: 36px;
  text-align: right;
`;

const BlockRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 8px;
  background: ${theme.colors.BorderGray50};
  border-radius: 6px;
`;

const BlockHeader = styled.div`
  font-size: 12px;
  font-weight: 600;
  color: ${theme.colors.DarkBlue400};
`;
